import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { usePermissions } from "../../../contexts/PermissionContext";
import { feesApi } from "../api/feesApi";

type RefundableReceipt = {
  id: string;
  receiptNo: string;
  studentId: string;
  studentName: string;
  scholarNumber: string;
  classSection: string;
  amountPaid: number;
  paymentMode: string;
  paymentModeCode: string;
  paymentDate: string;
  status: string;
  refundStatus: string;
  refundedAmount: number;
  availableRefund: number;
  refundBlocked?: boolean;
  refundBlockReason?: string | null;
};

type RefundHistoryRow = {
  id: string;
  refundReceiptNo: string;
  originalReceiptNo: string;
  studentName: string;
  scholarNumber: string;
  originalAmount: number;
  refundAmount: number;
  refundableAfter: number;
  refundMode: string;
  refundDate: string;
  reason: string;
  createdAt: string;
};

const REFUND_MODES = ["CASH", "UPI", "BANK_TRANSFER"];

const FeeRefundManagementPage: React.FC = () => {
  const { masters, fetchAcademicSessions, fetchMasters, fetchSections } = useMasterData();
  const { hasAnyPermission } = usePermissions();

  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [search, setSearch] = useState("");
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);

  const [refundableRows, setRefundableRows] = useState<RefundableReceipt[]>([]);
  const [historyRows, setHistoryRows] = useState<RefundHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [selectedReceipt, setSelectedReceipt] = useState<RefundableReceipt | null>(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMode, setRefundMode] = useState("CASH");
  const [refundDate, setRefundDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canView = useMemo(
    () => hasAnyPermission(["FEES_REFUND_VIEW", "FEES_VIEW", "FEE_VIEW", "FEE_MANAGE"]),
    [hasAnyPermission],
  );
  const canCreate = useMemo(
    () => hasAnyPermission(["FEES_REFUND_CREATE", "FEES_REFUND", "FEE_MANAGE", "FEES_MANAGE"]),
    [hasAnyPermission],
  );
  const canReport = useMemo(
    () => hasAnyPermission(["FEES_REFUND_REPORT", "FEES_VIEW", "FEE_VIEW", "FEE_MANAGE"]),
    [hasAnyPermission],
  );

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, [fetchMasters, fetchAcademicSessions]);

  useEffect(() => {
    const active = masters.academicSessions.find((s) => s.isActive);
    if (!sessionId && active) {
      setSessionId(active.id);
    }
  }, [masters.academicSessions, sessionId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!classId) {
        setSections([]);
        setSectionId("");
        return;
      }
      const next = await fetchSections(classId);
      if (!cancelled) {
        setSections(Array.isArray(next) ? next : []);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [classId, fetchSections]);

  const load = async () => {
    if (!canView || !sessionId) return;
    setLoading(true);
    setError(null);

    try {
      const [receipts, history] = await Promise.all([
        feesApi.refundableReceipts({
          sessionId,
          classId: classId || undefined,
          sectionId: sectionId || undefined,
          search: search || undefined,
        }),
        canReport
          ? feesApi.refundHistory({
              sessionId,
              classId: classId || undefined,
              sectionId: sectionId || undefined,
              search: search || undefined,
            })
          : Promise.resolve([]),
      ]);

      setRefundableRows(Array.isArray(receipts) ? receipts : []);
      setHistoryRows(Array.isArray(history) ? history : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load refund data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const selectReceipt = (row: RefundableReceipt) => {
    setSelectedReceipt(row);
    setRefundAmount("");
    setReason("");
    setRemarks("");
  };

  const processRefund = async () => {
    if (!selectedReceipt) {
      setError("Select a receipt first");
      return;
    }

    const amt = Number(refundAmount || 0);
    if (amt <= 0) {
      setError("Refund amount must be greater than 0");
      return;
    }
    if (amt > Number(selectedReceipt.availableRefund || 0)) {
      setError("Refund amount cannot exceed refundable balance");
      return;
    }
    if (!reason.trim()) {
      setError("Reason is required");
      return;
    }

    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const result = await feesApi.refund({
        originalReceiptId: selectedReceipt.id,
        studentId: selectedReceipt.studentId,
        refundAmount: amt,
        refundMode,
        refundDate,
        reason: reason.trim(),
        remarks: remarks.trim() || undefined,
      });

      setMessage(`Refund processed successfully. Refund Receipt: ${result?.refund?.refundReceiptNo || "Generated"}`);
      await load();

      const refreshed = refundableRows.find((r) => r.id === selectedReceipt.id);
      if (!refreshed || Number(refreshed.availableRefund || 0) <= amt) {
        setSelectedReceipt(null);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to process refund");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadRefundPdf = async (refundId: string) => {
    try {
      const file = await feesApi.getRefundPdfBlob(refundId);
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to download refund receipt");
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          Fee Refund Management
        </Typography>

        {!canView && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You do not have permission to view refund data.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Filter Panel
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Academic Session"
                fullWidth
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Class"
                fullWidth
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                {masters.classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Section"
                fullWidth
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                disabled={!classId}
              >
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Search (Name / Scholar No / Receipt No)"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="contained" fullWidth onClick={load} disabled={loading || !canView}>
                {loading ? "Loading..." : "Search"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Refundable Receipts
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Receipt No</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Class - Section</TableCell>
                <TableCell>Amount Paid</TableCell>
                <TableCell>Payment Mode</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Refunded</TableCell>
                <TableCell>Available Refund</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {refundableRows.map((row) => (
                <TableRow key={row.id} selected={selectedReceipt?.id === row.id} hover>
                  <TableCell>{row.receiptNo}</TableCell>
                  <TableCell>{row.studentName} ({row.scholarNumber || "-"})</TableCell>
                  <TableCell>{row.classSection}</TableCell>
                  <TableCell>{Number(row.amountPaid || 0).toFixed(2)}</TableCell>
                  <TableCell>{row.paymentMode || row.paymentModeCode || "-"}</TableCell>
                  <TableCell>{String(row.paymentDate || "").slice(0, 10)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Chip size="small" label={row.status} color={row.status === "CONFIRMED" ? "success" : "default"} />
                      <Chip size="small" label={row.refundStatus} color={row.refundStatus === "FULLY_REFUNDED" ? "warning" : "default"} />
                    </Stack>
                  </TableCell>
                  <TableCell>{Number(row.refundedAmount || 0).toFixed(2)}</TableCell>
                  <TableCell>{Number(row.availableRefund || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!canCreate || !!row.refundBlocked}
                      onClick={() => selectReceipt(row)}
                    >
                      {row.refundBlocked ? row.refundBlockReason || "Blocked" : "Refund"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!refundableRows.length && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary">
                      No refundable receipts found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Refund Form
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Student" value={selectedReceipt ? selectedReceipt.studentName : ""} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Receipt No" value={selectedReceipt ? selectedReceipt.receiptNo : ""} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Session" value={masters.academicSessions.find((s) => s.id === sessionId)?.name || ""} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Original Amount" value={selectedReceipt ? Number(selectedReceipt.amountPaid || 0).toFixed(2) : ""} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Already Refunded" value={selectedReceipt ? Number(selectedReceipt.refundedAmount || 0).toFixed(2) : ""} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Refundable Amount" value={selectedReceipt ? Number(selectedReceipt.availableRefund || 0).toFixed(2) : ""} InputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Refund Amount"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Refund Mode"
                value={refundMode}
                onChange={(e) => setRefundMode(e.target.value)}
              >
                {REFUND_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {mode}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Refund Date"
                value={refundDate}
                onChange={(e) => setRefundDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                fullWidth
                disabled={!canCreate || submitting || !selectedReceipt}
                onClick={processRefund}
              >
                {submitting ? "Processing..." : "Process Refund"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Refund History
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Refund Receipt No</TableCell>
                <TableCell>Original Receipt</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Original Amount</TableCell>
                <TableCell>Refund Amount</TableCell>
                <TableCell>Balance Refundable</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.refundReceiptNo}</TableCell>
                  <TableCell>{row.originalReceiptNo || "-"}</TableCell>
                  <TableCell>{row.studentName} ({row.scholarNumber || "-"})</TableCell>
                  <TableCell>{Number(row.originalAmount || 0).toFixed(2)}</TableCell>
                  <TableCell>{Number(row.refundAmount || 0).toFixed(2)}</TableCell>
                  <TableCell>{Number(row.refundableAfter || 0).toFixed(2)}</TableCell>
                  <TableCell>{row.refundMode}</TableCell>
                  <TableCell>{String(row.refundDate || row.createdAt || "").slice(0, 10)}</TableCell>
                  <TableCell>{row.reason}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => downloadRefundPdf(row.id)}>
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!historyRows.length && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary">
                      No refund history found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default FeeRefundManagementPage;
